import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy, effect, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { AuthService } from '~features/auth/auth.service';
import { SignupInfoDialogComponent } from '~features/brevo-auto-login/signup-info-dialog/signup-info-dialog.component';
import { CookieService } from '~features/cookies/cookie.service';
import { DialogService } from '~shared/services/dialog.service';

import { BrevoAutoLoginService, AutologinResult } from './brevo-auto-login.service';
import { SessionStorageService } from './session-storage.service';

@Injectable({
  providedIn: 'root',
})
export class BrevoAutoLoginRouterService implements OnDestroy {
  private router = inject(Router);
  private location = inject(Location);
  private newsletterTrackingService = inject(BrevoAutoLoginService);
  private sessionStorageService = inject(SessionStorageService);

  private routerSubscription: Subscription | null = null;
  private dialogService = inject(DialogService);
  private authService = inject(AuthService);
  cookieService = inject(CookieService);

  public needsEmptyHashDialog = signal(false);
  public needsNoParamsDialog = signal(false);

  constructor() {
    this.initializeAutologinEffect();
    this.initializeRouteDialogEffect();
  }

  /**
   * Expose Location service for URL cleanup in app.component.ts
   */
  getLocation(): Location {
    return this.location;
  }

  /**
   * Process newsletter tracking (called from app.component.ts)
   */
  processNewsletterTracking(hash: string): void {
    this.newsletterTrackingService.processNewsletterTracking(hash);
  }

  private initializeRouteDialogEffect(): void {
    effect(() => {
      const cookieToastVisible = this.cookieService.showCookieToast();

      // Check if we need to show empty hash dialog
      if (this.needsEmptyHashDialog() && !cookieToastVisible) {
        this.showSignupDialog('no_params');
        this.needsEmptyHashDialog.set(false); // Clear the need
      }

      // Check if we need to show no params dialog
      if (this.needsNoParamsDialog() && !cookieToastVisible) {
        this.showSignupDialog('no_params');
        this.needsNoParamsDialog.set(false); // Clear the need
      }
    });
  }
  private initializeAutologinEffect(): void {
    effect(() => {
      const result = this.newsletterTrackingService.autologinResult();
      const cookieToastVisible = this.cookieService.showCookieToast();

      // Only show autologin dialog if:
      // 1. We have an autologin result
      // 2. User is not authenticated
      // 3. Cookie toast is NOT visible
      if (result && !this.authService.isAuthenticated() && !cookieToastVisible) {
        this.handleAutologinResult(result);
      }
    });
  }

  private shouldSkipDialog(url: string): boolean {
    // Skip dialog for legal, auth, administrative, and external registration routes
    return (
      url.includes('/datenschutz') ||
      url.includes('/impressum') ||
      url.includes('/disclaimer') ||
      url.includes('/mifid2') ||
      url.includes('/setnewpassword') ||
      url.includes('/requestnewpassword') ||
      url.includes('/signup') ||
      url.includes('/admin') ||
      url.includes('/events/invite/')
    );
  }

  private handleRouteChange(event: NavigationEnd): void {
    // Get current URL and query parameters
    const currentUrl = event.url;
    const urlParams = new URLSearchParams(window.location.search);

    // Skip dialog for specific routes
    if (this.shouldSkipDialog(currentUrl)) {
      return;
    }

    const hash = urlParams.get('hash');
    const brevo = urlParams.get('brevo');

    // Scenario 1: Both hash and brevo are present and hash has content
    if (hash && hash.trim() !== '' && brevo === 'true') {
      // Process the tracking data - result will be handled by autologin effect
      this.newsletterTrackingService.processNewsletterTracking(hash);

      // Create new URL without tracking parameters
      const newUrl = new URL(currentUrl, window.location.origin);
      newUrl.searchParams.delete('hash');
      newUrl.searchParams.delete('brevo');

      // Get the path and any remaining query parameters
      const newPath = newUrl.pathname;
      const remainingParams = newUrl.searchParams.toString();
      const finalUrl = remainingParams ? `${newPath}?${remainingParams}` : newPath;

      // Update URL without triggering navigation event
      this.location.replaceState(finalUrl);
    }
    // Scenario 2: No hash (or empty hash) but brevo is present
    else if (brevo === 'true') {
      // Clean up URL even if hash is empty or missing
      const newUrl = new URL(currentUrl, window.location.origin);
      newUrl.searchParams.delete('hash');
      newUrl.searchParams.delete('brevo');

      const newPath = newUrl.pathname;
      const remainingParams = newUrl.searchParams.toString();
      const finalUrl = remainingParams ? `${newPath}?${remainingParams}` : newPath;

      // Clean up the URL without processing tracking
      this.location.replaceState(finalUrl);

      if (!hash || hash.trim() === '') {
        this.needsEmptyHashDialog.set(true);
      }
    }
    // Scenario 3: Neither hash nor brevo present - simplified logic
    else {
      // For unauthenticated users, set flag to show dialog
      if (!this.authService.isAuthenticated()) {
        this.needsNoParamsDialog.set(true);
      }
    }
  }

  private handleAutologinResult(result: AutologinResult): void {
    if (!result.success) {
      // Failed autologin - show dialog for all failure cases
      this.showSignupDialog('no_params');
    }
    // Successful autologin - no dialog needed
  }

  private showSignupDialog(status: string): void {
    if (!this.authService.isAuthenticated() && !this.sessionStorageService.hasSeenAccountInfo()) {
      this.sessionStorageService.setSeenAccountInfo();

      this.dialogService.open(
        SignupInfoDialogComponent,
        {
          data: {
            centered: true,
            size: 'lg',
            status: status,
          },
        },
        'dialog-panel-tile',
        true
      );
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
